/**
 * Catering Order Form
 * Handles multi-step navigation, validation, totals, and submission (email or cart mode)
 */

(function() {
  'use strict';

  // ============================================================================
  // Configuration & State
  // ============================================================================

  const CONFIG = {
    selectors: {
      form: '[data-catering-form]',
      step: '[data-catering-step]',
      progress: '[data-catering-progress]',
      progressStep: '[data-progress-step]',
      nextBtn: '[data-catering-next]',
      prevBtn: '[data-catering-prev]',
      reviewBtn: '[data-catering-review]',
      submitBtn: '[data-catering-submit]',
      qtyInput: '[data-catering-qty]',
      runningTotal: '[data-running-total]',
      reviewItems: '[data-review-items]',
      reviewContact: '[data-review-contact]',
      reviewTotals: '[data-review-totals]',
      deliveryOption: '[data-delivery-option]',
      requiredField: '[data-required]'
    },
    classes: {
      active: 'catering-step--active',
      progressActive: 'catering-progress__step--active',
      progressCompleted: 'catering-progress__step--completed',
      error: '--error',
      loading: 'catering-nav__btn--loading'
    },
    currency: {
      code: 'AUD',
      symbol: '$',
      locale: 'en-AU'
    }
  };

  let state = {
    currentStep: 0,
    totalSteps: 0,
    items: {},
    contactData: {},
    deliveryFee: 0,
    mode: 'email' // 'email' or 'cart'
  };

  // ============================================================================
  // Initialization
  // ============================================================================

  function init() {
    const form = document.querySelector(CONFIG.selectors.form);
    if (!form) return;

    state.mode = form.dataset.mode || 'email';
    state.totalSteps = form.querySelectorAll(CONFIG.selectors.step).length;

    bindEvents(form);
    updateProgress(form);
    calculateTotals(form);
  }

  function bindEvents(form) {
    // Navigation
    form.addEventListener('click', (e) => {
      if (e.target.closest(CONFIG.selectors.nextBtn)) {
        e.preventDefault();
        handleNext(form);
      }
      if (e.target.closest(CONFIG.selectors.prevBtn)) {
        e.preventDefault();
        handlePrev(form);
      }
      if (e.target.closest(CONFIG.selectors.reviewBtn)) {
        e.preventDefault();
        handleSkipToReview(form);
      }
      if (e.target.closest(CONFIG.selectors.submitBtn)) {
        e.preventDefault();
        handleSubmit(form);
      }
    });

    // Quantity changes
    form.addEventListener('input', (e) => {
      if (e.target.matches(CONFIG.selectors.qtyInput)) {
        handleQtyChange(e.target, form);
      }
    });

    form.addEventListener('change', (e) => {
      if (e.target.matches(CONFIG.selectors.qtyInput)) {
        normalizeQty(e.target, form);
      }
    });

    // Delivery option changes
    form.addEventListener('change', (e) => {
      if (e.target.matches(CONFIG.selectors.deliveryOption)) {
        handleDeliveryChange(e.target, form);
      }
    });

    // Form field validation on blur
    form.addEventListener('blur', (e) => {
      if (e.target.matches(CONFIG.selectors.requiredField)) {
        validateField(e.target);
      }
    }, true);
  }

  // ============================================================================
  // Navigation
  // ============================================================================

  function handleNext(form) {
    const currentStepEl = form.querySelector(`${CONFIG.selectors.step}.${CONFIG.classes.active}`);
    
    if (!validateStep(currentStepEl)) {
      return;
    }

    if (state.currentStep < state.totalSteps - 1) {
      state.currentStep++;
      showStep(form, state.currentStep);
      updateProgress(form);

      // If entering review step, populate review data
      const newStep = form.querySelectorAll(CONFIG.selectors.step)[state.currentStep];
      if (newStep.dataset.cateringStep === 'review') {
        populateReview(form);
      }
    }
  }

  function handlePrev(form) {
    if (state.currentStep > 0) {
      state.currentStep--;
      showStep(form, state.currentStep);
      updateProgress(form);
    }
  }

  function handleSkipToReview(form) {
    const currentStepEl = form.querySelector(`${CONFIG.selectors.step}.${CONFIG.classes.active}`);

    if (!validateStep(currentStepEl)) {
      return;
    }

    if (Object.keys(state.items).length === 0) {
      alert('Please select at least one item before reviewing your order.');
      return;
    }

    state.currentStep = state.totalSteps - 1;
    showStep(form, state.currentStep);
    updateProgress(form);
    populateReview(form);
  }

  function showStep(form, index) {
    const steps = form.querySelectorAll(CONFIG.selectors.step);
    steps.forEach((step, i) => {
      step.classList.toggle(CONFIG.classes.active, i === index);
    });

    // Scroll to top of form
    form.scrollIntoView({ behavior: 'smooth', block: 'start' });
    calculateTotals(form);
  }

  function updateProgress(form) {
    const progressSteps = form.querySelectorAll(CONFIG.selectors.progressStep);
    progressSteps.forEach((step, i) => {
      step.classList.remove(CONFIG.classes.progressActive, CONFIG.classes.progressCompleted);
      if (i < state.currentStep) {
        step.classList.add(CONFIG.classes.progressCompleted);
      } else if (i === state.currentStep) {
        step.classList.add(CONFIG.classes.progressActive);
      }
    });
  }

  // ============================================================================
  // Validation
  // ============================================================================

  function validateStep(stepEl) {
    if (!stepEl) return true;

    const stepType = stepEl.dataset.cateringStep;
    let isValid = true;

    // Validate required fields
    const requiredFields = stepEl.querySelectorAll(CONFIG.selectors.requiredField);
    requiredFields.forEach(field => {
      if (!validateField(field)) {
        isValid = false;
      }
    });

    // Validate minimum quantities for product steps
    if (stepType === 'products') {
      const qtyInputs = stepEl.querySelectorAll(CONFIG.selectors.qtyInput);
      qtyInputs.forEach(input => {
        if (!validateQty(input)) {
          isValid = false;
        }
      });
    }

    return isValid;
  }

  function validateField(field) {
    const value = field.value.trim();
    const type = field.type;
    let isValid = true;
    let errorMsg = '';

    // Required check
    if (field.hasAttribute('data-required') && !value) {
      isValid = false;
      errorMsg = 'This field is required';
    }

    // Email validation
    if (isValid && type === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        isValid = false;
        errorMsg = 'Please enter a valid email address';
      }
    }

    // Phone validation
    if (isValid && type === 'tel' && value) {
      const phoneRegex = /^[\d\s\-+()]{8,}$/;
      if (!phoneRegex.test(value)) {
        isValid = false;
        errorMsg = 'Please enter a valid phone number';
      }
    }

    // Update UI
    toggleFieldError(field, !isValid, errorMsg);
    return isValid;
  }

  function validateQty(input) {
    const value = parseInt(input.value) || 0;
    const min = parseInt(input.dataset.minQty) || 0;
    
    // If value is 0, that's fine (not ordering this item)
    if (value === 0) {
      toggleFieldError(input, false);
      return true;
    }

    // If value is less than minimum
    if (value > 0 && value < min) {
      toggleFieldError(input, true, `Minimum quantity: ${min}`);
      return false;
    }

    toggleFieldError(input, false);
    return true;
  }

  function toggleFieldError(field, hasError, message = '') {
    const errorClass = field.className.split(' ')[0] + CONFIG.classes.error;
    field.classList.toggle(errorClass, hasError);
    
    // Update or create error message
    let errorEl = field.parentNode.querySelector('.catering-field__error');
    if (hasError) {
      if (!errorEl) {
        errorEl = document.createElement('span');
        errorEl.className = 'catering-field__error';
        field.parentNode.appendChild(errorEl);
      }
      errorEl.textContent = message;
      errorEl.style.display = 'block';
    } else if (errorEl) {
      errorEl.style.display = 'none';
    }
  }

  // ============================================================================
  // Quantity & Totals
  // ============================================================================

  function handleQtyChange(input, form) {
    const productId = input.dataset.productId;
    const variantId = input.dataset.variantId;
    const price = parseFloat(input.dataset.price) || 0;
    const qty = parseInt(input.value) || 0;
    const name = input.dataset.productName;
    const serves = input.dataset.serves || '';
    const minQty = parseInt(input.dataset.minQty) || 0;

    // Validate quantity
    validateQty(input);

    // Update subtotal display
    const row = input.closest('tr, .catering-product');
    const subtotalEl = row?.querySelector('.catering-product__subtotal');
    if (subtotalEl) {
      const subtotal = qty * price;
      if (qty > 0) {
        subtotalEl.textContent = formatCurrency(subtotal);
        subtotalEl.classList.remove('catering-product__subtotal--empty');
      } else {
        subtotalEl.textContent = '-';
        subtotalEl.classList.add('catering-product__subtotal--empty');
      }
    }

    // Store item data
    const key = variantId || productId;
    if (qty > 0 && qty >= minQty) {
      state.items[key] = {
        productId,
        variantId,
        name,
        price,
        qty,
        serves,
        subtotal: qty * price
      };
    } else {
      delete state.items[key];
    }

    calculateTotals(form);
  }

  function normalizeQty(input, form) {
    const qty = parseInt(input.value) || 0;
    const minQty = parseInt(input.dataset.minQty) || 0;

    if (minQty > 0 && qty > 0 && qty < minQty) {
      input.value = minQty;
    }

    handleQtyChange(input, form);
  }

  function handleDeliveryChange(input, form) {
    state.deliveryFee = parseFloat(input.dataset.deliveryPrice) || 0;
    calculateTotals(form);
  }

  function calculateTotals(form) {
    const subtotal = Object.values(state.items).reduce((sum, item) => sum + item.subtotal, 0);
    const total = subtotal + state.deliveryFee;

    // Update every product-step total; the form renders one bar per collection step.
    form.querySelectorAll(CONFIG.selectors.runningTotal).forEach((runningTotalEl) => {
      runningTotalEl.textContent = formatCurrency(total);
    });

    return { subtotal, deliveryFee: state.deliveryFee, total };
  }

  // ============================================================================
  // Review Step
  // ============================================================================

  function populateReview(form) {
    populateReviewContact(form);
    populateReviewItems(form);
    populateReviewTotals(form);
  }

  function populateReviewContact(form) {
    const container = form.querySelector(CONFIG.selectors.reviewContact);
    if (!container) return;

    // Gather contact data from form fields
    const fields = {
      'First Name': form.querySelector('[name="contact[first_name]"]')?.value || '',
      'Last Name': form.querySelector('[name="contact[last_name]"]')?.value || '',
      'Email': form.querySelector('[name="contact[email]"]')?.value || '',
      'Phone': form.querySelector('[name="contact[phone]"]')?.value || '',
      'Company': form.querySelector('[name="contact[company]"]')?.value || '',
      'Event Date': form.querySelector('[name="contact[event_date]"]')?.value || '',
      'Event Time': getEventTime(form),
      'Delivery Address': form.querySelector('[name="contact[delivery_address]"]')?.value || '',
      'Delivery Option': getSelectedDeliveryOption(form)
    };

    state.contactData = fields;

    // Build HTML
    let html = '';
    for (const [label, value] of Object.entries(fields)) {
      if (value) {
        html += `
          <div class="catering-review__detail">
            <span class="catering-review__detail-label">${label}</span>
            <span class="catering-review__detail-value">${escapeHtml(value)}</span>
          </div>
        `;
      }
    }

    container.innerHTML = html;
  }

  function populateReviewItems(form) {
    const container = form.querySelector(CONFIG.selectors.reviewItems);
    if (!container) return;

    const items = Object.values(state.items);
    
    if (items.length === 0) {
      container.innerHTML = '<p class="catering-empty__text">No items selected</p>';
      return;
    }

    let html = '<ul class="catering-review__items">';
    items.forEach(item => {
      html += `
        <li class="catering-review__item">
          <span class="catering-review__item-name">${escapeHtml(item.name)}</span>
          <span class="catering-review__item-qty">× ${item.qty}</span>
          <span class="catering-review__item-price">${formatCurrency(item.subtotal)}</span>
        </li>
      `;
    });
    html += '</ul>';

    container.innerHTML = html;
  }

  function populateReviewTotals(form) {
    const container = form.querySelector(CONFIG.selectors.reviewTotals);
    if (!container) return;

    const { subtotal, deliveryFee, total } = calculateTotals(form);

    let html = `
      <div class="catering-review__total-row">
        <span>Subtotal</span>
        <span>${formatCurrency(subtotal)}</span>
      </div>
    `;

    if (deliveryFee > 0) {
      html += `
        <div class="catering-review__total-row">
          <span>Delivery</span>
          <span>${formatCurrency(deliveryFee)}</span>
        </div>
      `;
    }

    html += `
      <div class="catering-review__total-row catering-review__total-row--grand">
        <span>Total</span>
        <span>${formatCurrency(total)}</span>
      </div>
    `;

    container.innerHTML = html;
  }

  // ============================================================================
  // Submission
  // ============================================================================

  async function handleSubmit(form) {
    const submitBtn = form.querySelector(CONFIG.selectors.submitBtn);
    
    // Validate final step
    const currentStepEl = form.querySelector(`${CONFIG.selectors.step}.${CONFIG.classes.active}`);
    if (!validateStep(currentStepEl)) {
      return;
    }

    // Check if any items selected
    if (Object.keys(state.items).length === 0) {
      alert('Please select at least one item to order.');
      return;
    }

    // Show loading state
    form.classList.add('catering-form--submitting');
    submitBtn.classList.add(CONFIG.classes.loading);
    submitBtn.disabled = true;

    try {
      if (state.mode === 'cart') {
        await submitToCart(form);
      } else {
        await submitToEmail(form);
      }
    } catch (error) {
      console.error('Submission error:', error);
      form.classList.remove('catering-form--submitting');
      alert('There was an error submitting your order. Please try again.');
    } finally {
      submitBtn.classList.remove(CONFIG.classes.loading);
      submitBtn.disabled = false;
    }
  }

  async function submitToEmail(form) {
    // Build order details text
    const { subtotal, deliveryFee, total } = calculateTotals(form);
    let orderDetails = 'ORDER DETAILS\n';
    orderDetails += '='.repeat(50) + '\n\n';

    Object.values(state.items).forEach(item => {
      orderDetails += `${item.name}\n`;
      orderDetails += `  Qty: ${item.qty} × ${formatCurrency(item.price)} = ${formatCurrency(item.subtotal)}\n`;
      if (item.serves) {
        orderDetails += `  (${item.serves})\n`;
      }
      orderDetails += '\n';
    });

    orderDetails += '-'.repeat(50) + '\n';
    orderDetails += `Subtotal: ${formatCurrency(subtotal)}\n`;
    if (deliveryFee > 0) {
      orderDetails += `Delivery: ${formatCurrency(deliveryFee)}\n`;
    }
    orderDetails += `TOTAL: ${formatCurrency(total)}\n`;

    // Add notes
    const notes = form.querySelector('[name="contact[notes]"]')?.value || '';
    if (notes) {
      orderDetails += '\n' + '-'.repeat(50) + '\n';
      orderDetails += 'SPECIAL NOTES / DIETARY REQUIREMENTS:\n';
      orderDetails += notes;
    }

    const nativeForm = document.getElementById('CateringContactForm');
    const fieldsContainer = nativeForm?.querySelector('[data-catering-native-fields]');
    if (!nativeForm || !fieldsContainer) {
      throw new Error('Catering contact form is missing');
    }

    fieldsContainer.innerHTML = '';

    appendNativeContactField(fieldsContainer, 'contact[body]', orderDetails, 'textarea');

    if (typeof nativeForm.requestSubmit === 'function') {
      nativeForm.requestSubmit();
    } else {
      HTMLFormElement.prototype.submit.call(nativeForm);
    }
  }

  async function submitToCart(form) {
    // Clear cart first (optional - remove if you want to add to existing cart)
    // await fetch('/cart/clear.js', { method: 'POST' });

    // Build cart items
    const items = Object.values(state.items).map(item => ({
      id: item.variantId,
      quantity: item.qty,
      properties: {
        '_catering_order': 'true',
        'Serves': item.serves || ''
      }
    }));

    // Add delivery as line item if configured and selected
    const deliveryVariantId = form.dataset.deliveryVariantId;
    if (deliveryVariantId && state.deliveryFee > 0) {
      const selectedDelivery = form.querySelector(`${CONFIG.selectors.deliveryOption}:checked`);
      items.push({
        id: deliveryVariantId,
        quantity: 1,
        properties: {
          'Type': selectedDelivery?.dataset.deliveryLabel || 'Delivery'
        }
      });
    }

    // Add items to cart
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ items })
    });

    if (!response.ok) {
      throw new Error('Failed to add items to cart');
    }

    // Update cart attributes with contact info
    const attributes = {};
    for (const [key, value] of Object.entries(state.contactData)) {
      attributes[`catering_${key.toLowerCase().replace(/\s+/g, '_')}`] = value;
    }

    // Add notes
    const notes = form.querySelector('[name="contact[notes]"]')?.value || '';
    if (notes) {
      attributes['catering_notes'] = notes;
    }

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ attributes })
    });

    // Redirect to cart or checkout
    window.location.href = '/cart';
  }

  function showSuccess(form, mode) {
    const stepsContainer = form.querySelector('.catering-steps');
    const progressContainer = form.querySelector(CONFIG.selectors.progress);
    
    if (progressContainer) {
      progressContainer.style.display = 'none';
    }

    const message = mode === 'email' 
      ? 'We\'ve received your catering order request and will be in touch shortly to confirm.'
      : 'Your items have been added to cart. Proceed to checkout to complete your order.';

    stepsContainer.innerHTML = `
      <div class="catering-success">
        <div class="catering-success__icon">✓</div>
        <h2 class="catering-success__title">Order Submitted!</h2>
        <p class="catering-success__text">${message}</p>
        ${mode === 'cart' ? '<a href="/cart" class="catering-nav__btn catering-nav__btn--next" style="margin-top: 1.5rem; display: inline-flex;">View Cart →</a>' : ''}
      </div>
    `;
  }

  // ============================================================================
  // Helpers
  // ============================================================================

  function appendNativeContactField(container, name, value, type = 'input') {
    if (!value && name !== 'contact[body]') return;

    const field = document.createElement(type === 'textarea' ? 'textarea' : 'input');
    field.name = name;

    if (type !== 'textarea') {
      field.type = 'hidden';
    } else {
      field.hidden = true;
    }

    field.value = value || '';
    container.appendChild(field);
  }

  function formatCurrency(amount) {
    return new Intl.NumberFormat(CONFIG.currency.locale, {
      style: 'currency',
      currency: CONFIG.currency.code
    }).format(amount);
  }

  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  function getEventTime(form) {
    const hours = form.querySelector('[name="contact[event_time_hours]"]')?.value || '';
    const minutes = form.querySelector('[name="contact[event_time_minutes]"]')?.value || '';
    const period = form.querySelector('[name="contact[event_time_period]"]')?.value || '';
    
    if (hours && minutes) {
      return `${hours}:${minutes} ${period}`;
    }
    return '';
  }

  function getSelectedDeliveryOption(form) {
    const selected = form.querySelector(`${CONFIG.selectors.deliveryOption}:checked`);
    return selected?.dataset.deliveryLabel || '';
  }

  // ============================================================================
  // Initialize on DOM ready
  // ============================================================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
